import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import ShowMore from '@Components/Results/utils/ShowMore';
import SubstanceQuantitativeTable from '@Components/Results/substances/tables/quantitative/SubstanceQuantitativeTable';
import TableColumnsSwitcher from '@Components/Results/reactions/reaction-details/table-columns-switcher/TableColumnsSwitcher';
import SubstanceQuantitativeReport from '@Components/Results/substances/tables/quantitative/substance-quantitative-report.component';
import SubstanceData from '@Services/SubstanceData';
import { HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME } from '@Pages/Heatmap/HeatmapCellDetailsDialog/constants';

import './SubstanceQuantitativeData.scss';

function SubstanceQuantitativeData({
  data: initialData,
  uniqueId,
  metadata,
  total,
  showTitle,
  initialItemsAmount = 7,
  columnsViewedByDefault,
  retrieveData,
  track,
}) {
  const isReport = initialData.layout === 'list';
  const id = `${uniqueId}-quantitative`;
  const containerRef = useRef(null);
  const columnSwitcherRef = useRef(null);
  const cacheRef = useRef(null);

  function processData(inputData) {
    if (isReport) {
      return {
        headers: inputData.headers,
        rows: SubstanceData.buildReportData(inputData.headers, inputData.rows),
      };
    }
    return SubstanceData.alterDataShownQuan(inputData);
  }

  const [data, setData] = useState(() => {
    if (initialData.headers && initialData.rows) {
      return processData(initialData);
    }
    return null;
  });
  const [headersReady, setHeadersReady] = useState(isReport);
  const [isShowLess, setIsShowLess] = useState(false);
  const [error, setError] = useState(!(initialData.headers && initialData.rows));

  const updateHeaders = useCallback(
    (headers) => {
      if (!headersReady) Spinners.terminate(id);
      setData((prevData) => ({
        ...prevData,
        headers,
      }));
      setHeadersReady(true);
    },
    [headersReady, id]
  );

  const showAllNonEmptyCols = useCallback(() => {
    if (columnSwitcherRef.current) {
      columnSwitcherRef.current.showAllNonEmptyCols();
    }
  }, []);

  function appendRows(rows) {
    setData((prevData) => processData({ ...prevData, rows: prevData.rows?.concat(rows) }));
  }

  const showMore = useCallback(
    (firstItem = 1, lastItem = initialItemsAmount) =>
      new Promise((resolve, reject) => {
        if (cacheRef.current) {
          appendRows(cacheRef.current);
          cacheRef.current = null;
          resolve();
          return;
        }
        Spinners.activate(id);
        retrieveData(firstItem, lastItem)
          .then(({ rows }) => {
            appendRows(rows);
            if (track) track(rows, 'showMoreQuan', null, firstItem, lastItem);
            return rows;
          })
          .catch(() => reject(new Error('No quantitative data found')))
          .finally(() => {
            Spinners.terminate(id);
            resolve();
          });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, initialItemsAmount, retrieveData, track]
  );

  const showLess = useCallback(() => {
    setData((prevData) => {
      const newData = { ...prevData };
      cacheRef.current = newData.rows?.splice(initialItemsAmount);
      newData.rows = [...newData.rows];
      return newData;
    });
  }, [initialItemsAmount]);

  useEffect(() => {
    if (!isReport && initialData.headers && initialData.rows) {
      Spinners.activate(id);
    }
    if (!(initialData.headers && initialData.rows)) {
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollContainerSelector = metadata.heatmapMode ? `.${HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME}` : null;

  if (error) {
    return <div className="substance-results-quantitative-error">Information currently unavailable.</div>;
  }

  return (
    <div className="substance-results-quantitative" id={id} ref={containerRef}>
      {(showTitle || !isReport) && (
        <div className="substance-results-quantitative__header">
          {showTitle && (
            <div className="u-els-font-size-base u-els-line-height-default substance-results-quantitative__title">
              <strong>Quantitative Results</strong>
            </div>
          )}
          {!isReport && (
            <div className="column-switcher-container u-els-display-flex">
              <TableColumnsSwitcher
                headers={data?.headers}
                uniqueId={id}
                containerRef={containerRef}
                ref={columnSwitcherRef}
                updateHeaders={updateHeaders}
                defaultColumns={columnsViewedByDefault}
                scrollable={metadata.heatmapMode}
              />
            </div>
          )}
        </div>
      )}

      {!isReport && headersReady && (
        <SubstanceQuantitativeTable metadata={metadata} data={data} uniqueId={id} showAllNonEmptyCols={showAllNonEmptyCols} />
      )}
      {isReport && <SubstanceQuantitativeReport metadata={metadata} data={data.rows} total={total} />}

      <RxSpinnerComponent spinnerId={id} position="relative" />

      <div className={`rx-btn-group--center ${isShowLess ? 'show-less-sticky' : ''}`}>
        <ShowMore
          hideIcon
          initial={initialItemsAmount}
          scrollContainerSelector={scrollContainerSelector}
          metadata={metadata}
          current={data?.rows?.length}
          total={total}
          container={`#${id}`}
          moreHandler={({ firstItem, lastItem }) => {
            setIsShowLess(true);
            return showMore(firstItem, lastItem);
          }}
          lessHandler={() => {
            setIsShowLess(false);
            return showLess();
          }}
        />
      </div>
    </div>
  );
}

SubstanceQuantitativeData.propTypes = {
  metadata: MetadataPropTypesShape,
  total: PropTypes.number.isRequired,
  data: PropTypes.shape({
    headers: PropTypes.array,
    rows: PropTypes.array,
    layout: PropTypes.string,
  }).isRequired,
  uniqueId: PropTypes.string.isRequired,
  retrieveData: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  initialItemsAmount: PropTypes.number,
  columnsViewedByDefault: PropTypes.array,
  track: PropTypes.func,
};

export default SubstanceQuantitativeData;
